import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { useIssues } from "../../controllers/useIssues";
import type { ProjectMember } from "../../models/account";
import type { Issue, IssuePriority, IssueStatus, IssueType } from "../../models/project";
import type { WorkAreaType } from "../../models/eqms";
import { WORK_AREAS, WORK_AREA_SHORT_LABEL, workAreaLabel } from "../../models/eqms";
import { issuePriorities, issueTypes } from "../../controllers/projectApi";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { initials, type UserDir } from "../components/UserCell";
import { useSavedViews } from "../../controllers/useSavedViews";
import { IssueDetailView } from "./IssueDetailView";
import { GanttView } from "./GanttView";
import {
  activeFilterCount,
  applyFilters,
  distinctLabels,
  emptyFilter,
  groupIssues,
  PRIORITY_META,
  PRIORITY_ORDER,
  rollupSubtasks,
  sortIssues,
  STATUS_META,
  STATUS_ORDER,
  TRACE_TYPES,
  TYPE_LABEL,
  UNASSIGNED,
  type BoardColumn,
  type FilterState,
  type GroupAxis,
  type SavedView,
  type SortKey,
  type SubtaskRollup,
} from "../../controllers/issueViewModel";

type IssuesCtl = ReturnType<typeof useIssues>;

interface Scope {
  workArea?: WorkAreaType;
  sprintId?: string | "active";
}

/**
 * A project's issue tracker, GitHub-Projects style: saved-view tabs (board / priority / table / roadmap
 * / mine), a keyword+field filter bar, a group-by board with rich cards (priority label, assignee
 * avatar, sub-issue progress), a sortable table, and a roadmap. All filtering/grouping/rollup is
 * client-side over the already-loaded issue set; drag between columns writes through the existing
 * status/priority/assignee/work-area mutations. `scope` locks the view to a work area or sprint lens.
 */
export function IssuesView({
  issues,
  members,
  scope,
  currentUserId,
  defaultWorkAreas = [],
}: {
  issues: IssuesCtl;
  members: ProjectMember[];
  scope?: Scope;
  currentUserId?: string;
  /** @deprecated kept for call-site compat; use `scope.workArea`. */
  defaultWorkAreas?: WorkAreaType[];
}) {
  const userDir = useUserDirectory(issues.getToken);
  const scopeArea = scope?.workArea ?? defaultWorkAreas[0];

  useEffect(() => {
    userDir.ensure([...members.map((m) => m.userId), ...issues.issues.map((i) => i.assigneeId)]);
  }, [members, issues.issues, userDir]);

  // ---- scope pre-filter (work area / sprint lens) applied before the user's own filters ----
  const scopedIssues = useMemo(() => {
    const activeSprintId = issues.activeSprint?.id;
    return issues.issues.filter((i) => {
      if (scopeArea && !i.workAreaTypes.includes(scopeArea)) return false;
      if (scope?.sprintId === "active") return activeSprintId ? i.sprintId === activeSprintId : true;
      if (scope?.sprintId && scope.sprintId !== "active") return i.sprintId === scope.sprintId;
      return true;
    });
  }, [issues.issues, issues.activeSprint, scopeArea, scope?.sprintId]);

  // sub-issue rollup computed from the widest list this controller holds (see rollupSubtasks note)
  const rollup = useMemo(() => rollupSubtasks(issues.issues), [issues.issues]);
  const labelFor = useCallback((id: string) => userDir.label(id), [userDir]);

  // ---- saved views (persistence lives in the useSavedViews controller) ----
  const scopeKey = scopeArea ?? scope?.sprintId ?? "all";
  const { views: allViews, builtins, addView, removeView } = useSavedViews(issues.projectId, scopeKey, currentUserId);

  const [activeViewId, setActiveViewId] = useState<string>(scopeArea ? "table" : "board");
  const activeView = allViews.find((v) => v.id === activeViewId) ?? builtins[0];

  // ---- working (ephemeral) view state, seeded from the active saved view ----
  const [view, setView] = useState(activeView.view);
  const [groupBy, setGroupBy] = useState<GroupAxis>(activeView.groupBy);
  const [filter, setFilter] = useState<FilterState>(activeView.filter);
  const [sortKey, setSortKey] = useState<SortKey>(activeView.sortKey ?? "priority");
  const [sortDir, setSortDir] = useState<"asc" | "desc">(activeView.sortDir ?? "desc");

  function selectView(v: SavedView) {
    setActiveViewId(v.id);
    setView(v.view);
    setGroupBy(v.groupBy);
    setFilter(v.filter);
    setSortKey(v.sortKey ?? "priority");
    setSortDir(v.sortDir ?? "desc");
  }

  function saveCurrentAsView() {
    const name = window.prompt("새 뷰 이름")?.trim();
    if (!name) return;
    const v: SavedView = { id: `c-${Date.now()}`, name, view, groupBy, filter, sortKey, sortDir };
    addView(v);
    setActiveViewId(v.id);
  }
  function deleteView(id: string) {
    if (!window.confirm("이 뷰를 삭제할까요?")) return;
    removeView(id);
    if (activeViewId === id) selectView(builtins[0]);
  }

  const [creating, setCreating] = useState<{ status?: IssueStatus; priority?: IssuePriority } | null>(null);

  const filtered = useMemo(() => applyFilters(scopedIssues, filter), [scopedIssues, filter]);
  const memberIds = useMemo(() => members.map((m) => m.userId), [members]);

  // drill-down into a single issue. NOTE: every hook must run before this early return — React
  // requires a stable hook count across renders, and returning above the useMemos below used to
  // crash the whole app ("Rendered fewer hooks than expected") the moment an issue was opened.
  if (issues.screen === "detail" && issues.selectedIssue) {
    return <IssueDetailView issues={issues} members={members} />;
  }

  return (
    <div>
      <ViewTabs views={allViews} activeId={activeViewId} onSelect={selectView} onNew={saveCurrentAsView} onDelete={deleteView} />

      <FilterBar
        filter={filter}
        onChange={setFilter}
        issues={scopedIssues}
        members={members}
        userDir={userDir}
        lockedWorkArea={scopeArea}
        groupBy={groupBy}
        onGroupBy={setGroupBy}
        showGroupBy={view === "board"}
        onNewIssue={() => setCreating({})}
      />

      {issues.error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {issues.error}
        </div>
      )}

      {creating && (
        <CreateIssueForm
          issues={issues}
          defaultWorkAreas={scopeArea ? [scopeArea] : defaultWorkAreas}
          preset={creating}
          onDone={() => setCreating(null)}
        />
      )}

      {issues.loading ? (
        <div className="center">불러오는 중…</div>
      ) : view === "roadmap" ? (
        <GanttView issues={issues} />
      ) : view === "table" ? (
        <TableView
          list={sortIssues(filtered, sortKey, sortDir, labelFor)}
          sortKey={sortKey} sortDir={sortDir}
          onSort={(k) => { if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("asc"); } }}
          onOpen={issues.openIssue} userDir={userDir} rollup={rollup}
        />
      ) : (
        <BoardView
          issues={issues} groupBy={groupBy} filtered={filtered}
          memberIds={memberIds} labelFor={labelFor} userDir={userDir} rollup={rollup}
          hideWorkArea={!!scopeArea}
          onAdd={(preset) => setCreating(preset)}
        />
      )}
    </div>
  );
}

// ---- saved-view tabs ----
function ViewTabs({ views, activeId, onSelect, onNew, onDelete }: {
  views: SavedView[]; activeId: string; onSelect: (v: SavedView) => void; onNew: () => void; onDelete: (id: string) => void;
}) {
  return (
    <div className="tabs" style={{ margin: "0 0 14px", flexWrap: "wrap", gap: 16 }}>
      {views.map((v) => (
        <span key={v.id} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
          <button className={`tab ${activeId === v.id ? "active" : ""}`} onClick={() => onSelect(v)}>{v.name}</button>
          {!v.builtin && (
            <button className="link-btn" title="뷰 삭제" style={{ padding: "0 2px" }} onClick={() => onDelete(v.id)}>✕</button>
          )}
        </span>
      ))}
      <button className="tab" title="현재 필터·그룹을 새 뷰로 저장 (이 브라우저에만 저장)" onClick={onNew}>＋ 새 뷰</button>
    </div>
  );
}

// ---- filter bar ----
function FilterBar({
  filter, onChange, issues, members, userDir, lockedWorkArea, groupBy, onGroupBy, showGroupBy, onNewIssue,
}: {
  filter: FilterState; onChange: (f: FilterState) => void; issues: Issue[]; members: ProjectMember[];
  userDir: UserDir; lockedWorkArea?: WorkAreaType; groupBy: GroupAxis; onGroupBy: (g: GroupAxis) => void;
  showGroupBy: boolean; onNewIssue: () => void;
}) {
  const labels = useMemo(() => distinctLabels(issues), [issues]);
  const count = activeFilterCount(filter);
  const set = (patch: Partial<FilterState>) => onChange({ ...filter, ...patch });

  return (
    <div className="filter-bar">
      <div className="search">
        🔍
        <input placeholder="키워드로 필터 (키·제목)" value={filter.keyword}
          onChange={(e) => set({ keyword: e.target.value })} />
      </div>

      <FilterDropdown label="상태" options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label }))}
        selected={filter.statuses} onToggle={(v) => set({ statuses: toggle(filter.statuses, v as IssueStatus) })} />
      <FilterDropdown label="우선순위" options={PRIORITY_ORDER.map((p) => ({ value: p, label: PRIORITY_META[p].label }))}
        selected={filter.priorities} onToggle={(v) => set({ priorities: toggle(filter.priorities, v as IssuePriority) })} />
      <FilterDropdown label="담당자"
        options={[...members.map((m) => ({ value: m.userId, label: userDir.label(m.userId) })), { value: UNASSIGNED, label: "미배정" }]}
        selected={filter.assignees} onToggle={(v) => set({ assignees: toggle(filter.assignees, v) })} />
      <FilterDropdown label="유형" options={(Object.keys(TYPE_LABEL) as IssueType[]).map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
        selected={filter.types} onToggle={(v) => set({ types: toggle(filter.types, v as IssueType) })} />
      {lockedWorkArea ? (
        <span className="filter-chip locked" title="이 화면은 업무유형이 고정되어 있습니다">🔒 {WORK_AREA_SHORT_LABEL[lockedWorkArea]}</span>
      ) : (
        <FilterDropdown label="업무유형" options={WORK_AREAS.map((a) => ({ value: a, label: WORK_AREA_SHORT_LABEL[a] }))}
          selected={filter.workAreas} onToggle={(v) => set({ workAreas: toggle(filter.workAreas, v) })} />
      )}
      {labels.length > 0 && (
        <FilterDropdown label="레이블" options={labels.map((l) => ({ value: l, label: l }))}
          selected={filter.labels} onToggle={(v) => set({ labels: toggle(filter.labels, v) })} />
      )}

      {count > 0 && (
        <button className="filter-chip active" onClick={() => onChange(emptyFilter())}>
          필터 {count} · 전체 해제 ✕
        </button>
      )}

      <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
        {showGroupBy && (
          <label style={{ fontSize: 13, color: "var(--ink3)", display: "inline-flex", gap: 6, alignItems: "center" }}>
            그룹
            <select className="sdui-input" style={{ width: "auto", padding: "4px 8px" }} value={groupBy}
              onChange={(e) => onGroupBy(e.target.value as GroupAxis)}>
              <option value="status">상태</option>
              <option value="priority">우선순위</option>
              <option value="assignee">담당자</option>
              <option value="workArea">업무유형</option>
            </select>
          </label>
        )}
        <button className="btn primary" style={{ width: "auto" }} onClick={onNewIssue}>＋ 이슈</button>
      </div>
    </div>
  );
}

function FilterDropdown({ label, options, selected, onToggle }: {
  label: string; options: { value: string; label: string }[]; selected: string[]; onToggle: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const n = selected.length;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className={`filter-chip ${n > 0 ? "active" : ""}`} onClick={() => setOpen((o) => !o)}>
        {label}{n > 0 ? ` · ${n}` : ""} ▾
      </button>
      {open && (
        <div className="filter-menu">
          {options.map((o) => (
            <label key={o.value} className="filter-opt">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onToggle(o.value)} />
              {o.label}
            </label>
          ))}
          {options.length === 0 && <div style={{ padding: 8, color: "var(--ink3)", fontSize: 13 }}>없음</div>}
        </div>
      )}
    </div>
  );
}

// ---- board ----
function BoardView({ issues, groupBy, filtered, memberIds, labelFor, userDir, rollup, hideWorkArea, onAdd }: {
  issues: IssuesCtl; groupBy: GroupAxis; filtered: Issue[]; memberIds: string[];
  labelFor: (id: string) => string; userDir: UserDir; rollup: Map<string, SubtaskRollup>;
  hideWorkArea: boolean; onAdd: (preset: { status?: IssueStatus; priority?: IssuePriority }) => void;
}) {
  const columns = useMemo(() => groupIssues(filtered, groupBy, memberIds, labelFor), [filtered, groupBy, memberIds, labelFor]);
  const [dropKey, setDropKey] = useState<string | null>(null);

  function drop(col: BoardColumn, issueId: string) {
    setDropKey(null);
    if (groupBy === "status" && col.presetStatus) void issues.setStatus(issueId, col.presetStatus);
    else if (groupBy === "priority" && col.presetPriority) void issues.setPriority(issueId, col.presetPriority);
    else if (groupBy === "assignee") void issues.setAssignee(issueId, col.key === UNASSIGNED ? null : col.key);
    else if (groupBy === "workArea") {
      // work area is multi-valued: dropping onto a lane ADDS that area (keeps the others) rather than
      // silently replacing the whole set; dropping onto 미지정 clears it.
      const cur = filtered.find((i) => i.id === issueId)?.workAreaTypes ?? [];
      const next = col.key === "__none__" ? [] : [...new Set([...cur, col.key])];
      void issues.setWorkAreas(issueId, next);
    }
  }

  return (
    <div className="board">
      {columns.map((col) => (
        <div
          key={col.key}
          className={`board-col ${dropKey === col.key ? "drop-target" : ""}`}
          onDragOver={(e) => { e.preventDefault(); if (dropKey !== col.key) setDropKey(col.key); }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropKey(null); }}
          onDrop={(e) => { const id = e.dataTransfer.getData("text/issue"); if (id) drop(col, id); }}
        >
          <div className="board-col-title">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {col.cls && <span className={`st-dot ${col.cls}`} />}
              {col.label}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span className="tag">{col.items.length}</span>
              <button className="board-col-add" title="이 컬럼에 이슈 추가"
                onClick={() => onAdd({ status: col.presetStatus, priority: col.presetPriority })}>＋</button>
            </span>
          </div>
          {col.desc && <div className="board-col-desc">{col.desc}</div>}
          {col.items.map((issue) => (
            <RichIssueCard key={issue.id} issue={issue} onOpen={() => issues.openIssue(issue.id)}
              userDir={userDir} rollup={rollup.get(issue.id)} hideWorkArea={hideWorkArea} hideStatusDot={groupBy === "status"} />
          ))}
          {col.items.length === 0 && <div className="sdui-list-empty" style={{ padding: 16, border: "1px dashed var(--border)", borderRadius: 8 }}>이슈 없음</div>}
        </div>
      ))}
    </div>
  );
}

// ---- rich issue card ----
function RichIssueCard({ issue, onOpen, userDir, rollup, hideWorkArea, hideStatusDot }: {
  issue: Issue; onOpen: () => void; userDir: UserDir; rollup?: SubtaskRollup; hideWorkArea: boolean; hideStatusDot: boolean;
}) {
  const overdue = !!issue.dueDate && issue.status !== "DONE" && issue.dueDate < today();
  const stripe = issue.priority === "HIGHEST" ? "stripe-highest" : issue.priority === "HIGH" ? "stripe-high" : "";
  return (
    <div
      className={`board-card ${stripe}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/issue", issue.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
    >
      <div className="card-top">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          {!hideStatusDot && <span className={`st-dot ${STATUS_META[issue.status].cls}`} title={STATUS_META[issue.status].label} />}
          <span className="issue-key">{issue.key}</span>
          <TypeBadge type={issue.type} />
        </span>
        <Avatar userDir={userDir} id={issue.assigneeId} />
      </div>
      <div className="issue-summary" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {issue.summary}
      </div>
      <div className="issue-meta-row">
        <PriorityLabel priority={issue.priority} />
        {issue.storyPoints != null && <span className="tag">{issue.storyPoints}pt</span>}
        {issue.labels.slice(0, 2).map((l) => <span key={l} className="tag">#{l}</span>)}
        {issue.labels.length > 2 && <span className="tag">+{issue.labels.length - 2}</span>}
        {!hideWorkArea && issue.workAreaTypes.map((a) => <span key={a} className="tag">{workAreaLabel(a)}</span>)}
      </div>
      {rollup && rollup.total > 0 && (
        <div className={`issue-progress ${rollup.pct === 100 ? "done" : ""}`}>
          <span>하위 {rollup.done}/{rollup.total}</span>
          <span className="bar"><i style={{ width: `${rollup.pct}%` }} /></span>
          <span>{rollup.pct}%</span>
        </div>
      )}
      {issue.dueDate && (
        <div className={`due ${overdue ? "overdue" : ""}`}>📅 {issue.dueDate.slice(5)}{overdue ? " ⚠" : ""}</div>
      )}
    </div>
  );
}

function TypeBadge({ type }: { type: IssueType }) {
  return <span className={`sdui-badge ${TRACE_TYPES.has(type) ? "trace" : ""}`}>{TYPE_LABEL[type]}</span>;
}

function PriorityLabel({ priority }: { priority: IssuePriority }) {
  const m = PRIORITY_META[priority];
  return <span className={`pri ${m.cls}`}>{m.glyph} {m.label}</span>;
}

function Avatar({ userDir, id }: { userDir: UserDir; id?: string | null }) {
  if (!id) return <span className="avatar sm empty" title="미배정">＋</span>;
  const name = userDir.label(id);
  return <span className="avatar sm" title={name}>{initials(name)}</span>;
}

// ---- table ----
function TableView({ list, sortKey, sortDir, onSort, onOpen, userDir, rollup }: {
  list: Issue[]; sortKey: SortKey; sortDir: "asc" | "desc"; onSort: (k: SortKey) => void;
  onOpen: (id: string) => void; userDir: UserDir; rollup: Map<string, SubtaskRollup>;
}) {
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : "");
  const th = (k: SortKey, label: string) => (
    <th className="sortable" onClick={() => onSort(k)}>{label}{arrow(k)}</th>
  );
  return (
    <div style={{ overflowX: "auto" }}>
      <div className="sdui-table-wrap">
    <table className="sdui-table tbl-title2">
        <thead>
          <tr>
            {th("number", "키")}{th("summary", "제목")}{th("status", "상태")}{th("priority", "우선순위")}
            {th("assignee", "담당자")}{th("points", "pt")}{th("dueDate", "마감")}
          </tr>
        </thead>
        <tbody>
          {list.map((i) => {
            const r = rollup.get(i.id);
            return (
              <tr key={i.id} style={{ cursor: "pointer" }} onClick={() => onOpen(i.id)}>
                <td className="mono">{i.key}</td>
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <TypeBadge type={i.type} /> {i.summary}
                    {r && r.total > 0 && <span className="tag" title="하위 이슈">{r.done}/{r.total}</span>}
                  </span>
                </td>
                <td><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className={`st-dot ${STATUS_META[i.status].cls}`} />{STATUS_META[i.status].label}</span></td>
                <td><PriorityLabel priority={i.priority} /></td>
                <td>{i.assigneeId ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Avatar userDir={userDir} id={i.assigneeId} />{userDir.label(i.assigneeId)}</span> : <span className="mono">미배정</span>}</td>
                <td>{i.storyPoints ?? ""}</td>
                <td><span className={`due ${i.dueDate && i.status !== "DONE" && i.dueDate < today() ? "overdue" : ""}`}>{i.dueDate ? i.dueDate.slice(5) : ""}</span></td>
              </tr>
            );
          })}
        </tbody>
        {list.length === 0 && <tfoot><tr><td colSpan={7} className="sdui-table-empty">조건에 맞는 이슈가 없습니다.</td></tr></tfoot>}
      </table>
      </div>
    </div>
  );
}

// ---- helpers ----
function toggle<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Chip-toggle multiselect (0..n) over the four work areas. */
export function WorkAreaMultiSelect({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const flip = (a: WorkAreaType) => onChange(value.includes(a) ? value.filter((v) => v !== a) : [...value, a]);
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {WORK_AREAS.map((a) => {
        const on = value.includes(a);
        return (
          <button key={a} type="button" className="tag"
            style={{ cursor: "pointer", border: on ? "1.5px solid var(--blue)" : "1px solid var(--border)", color: on ? "var(--blue)" : undefined }}
            onClick={() => flip(a)}>
            {on ? "✓ " : ""}{WORK_AREA_SHORT_LABEL[a]}
          </button>
        );
      })}
    </div>
  );
}

function CreateIssueForm({ issues, defaultWorkAreas, preset, onDone }: {
  issues: IssuesCtl;
  defaultWorkAreas: WorkAreaType[];
  preset: { status?: IssueStatus; priority?: IssuePriority };
  onDone: () => void;
}) {
  const [type, setType] = useState<IssueType>("TASK");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<IssuePriority>(preset.priority ?? "MEDIUM");
  const [workAreaTypes, setWorkAreaTypes] = useState<string[]>(defaultWorkAreas);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!summary.trim()) return;
    setBusy(true);
    try {
      const created = await issues.addIssue({
        type, summary, description: description || undefined, priority,
        workAreaTypes: workAreaTypes.length ? workAreaTypes : undefined,
      });
      if (created) {
        if (preset.status && preset.status !== created.status) await issues.setStatus(created.id, preset.status);
        setSummary(""); setDescription("");
        onDone();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="info-card" style={{ marginBottom: 16 }}>
      <h4>새 이슈{preset.status ? ` · ${STATUS_META[preset.status].label}` : preset.priority ? ` · ${PRIORITY_META[preset.priority].label}` : ""}</h4>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label className="sdui-field" style={{ minWidth: 120 }}>
          <span className="sdui-label">유형</span>
          <select className="sdui-input" value={type} onChange={(e) => setType(e.target.value as IssueType)}>
            {issueTypes.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
        </label>
        <label className="sdui-field" style={{ minWidth: 120 }}>
          <span className="sdui-label">우선순위</span>
          <select className="sdui-input" value={priority} onChange={(e) => setPriority(e.target.value as IssuePriority)}>
            {issuePriorities.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
          </select>
        </label>
        <label className="sdui-field" style={{ flex: 2, minWidth: 220 }}>
          <span className="sdui-label">요약</span>
          <input className="sdui-input" value={summary} onChange={(e) => setSummary(e.target.value)} autoFocus />
        </label>
        <button className="btn primary" style={{ width: "auto" }} disabled={!summary.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "생성"}
        </button>
        <button className="btn" style={{ width: "auto" }} onClick={onDone}>취소</button>
      </div>
      <label className="sdui-field" style={{ marginTop: 10 }}>
        <span className="sdui-label">업무유형 (선택 · 복수 가능)</span>
        <WorkAreaMultiSelect value={workAreaTypes} onChange={setWorkAreaTypes} />
      </label>
      <label className="sdui-field" style={{ marginTop: 10 }}>
        <span className="sdui-label">설명 (선택)</span>
        <textarea className="sdui-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
    </div>
  );
}
